import { RowComponent, RowLookup, TabulatorFull as Tabulator } from "tabulator-tables";
import * as XLSX from "xlsx";

(window as any).XLSX = XLSX;

export class TabulatorGrid {
    grid!: any;
    ref: any | undefined;
    context: any | undefined;
    gridOptions: any | undefined;
    reload: boolean | undefined;
    originalData: any | undefined;
    isReady: boolean = false;
    isLoading: boolean = false;
    selectedRows: any[] = [];
    isSelecting: boolean = false;
    private readonly DELTA_PERCENT_KEY = "DeltaPercent";
    private readonly DELTA_VALUE_KEY = "DeltaValue";
    private readonly DELTA_PERCENT_DEFAULT_TITLE = "Delta %";
    private readonly DELTA_VALUE_DEFAULT_TITLE = "Delta value";
    private onResize!: () => void;

    createGridByRef(ref: any, context: any) {
        const me = this;
        this.ref = ref;
        this.context = context;

        this.onResize = this.resizeToParent.bind(this);
        window.addEventListener("resize", this.onResize, { passive: true });

        const rowClickEnabled = context.behaviorConfig?.rowClickEnabled ?? false;

        this.gridOptions = {
            maxHeight: `calc(100% - ${this.heightAdjuster(context)}px)`,
            layout: this.context.layout, //'fitDataTable', //'fitDataStretch', //'fitDataFill', //'fitData', //"fitColumns",
            columnHeaderVertAlign: 'bottom',
            columnDefaults: {
                tooltip: true,
            },
            clipboard: true,
            resizableColumns: true,
            movableColumns: !(context.isPivot || context.grouping),
            layoutColumnsOnNewData: false,
            dataTree: context.grouping,
            dataTreeStartExpanded: false,
            dataTreeChildIndent: context.groupTreeChildIndent,
            dataTreeBranchElement: true,
            dataTreeCollapseElement: '<i class="material-icons">expand_less</i>',
            dataTreeExpandElement: '<i class="material-icons">expand_more</i>',
            reactiveData: false,
            headerSort: context.interactionEnabled,
            columnHeaderSortMulti: context.interactionEnabled,
            headerSortElement: '<i class="material-icons">arrow_drop_up</i>', // Flipped in css on sort
            sortOrderReverse: true, // Imitate hoe Excel sorts
            selectableRows: rowClickEnabled,
            selectableRollingSelection: false,
            selectablePersistence: true,
            invalidOptionWarnings: false,
            nestedFieldSeparator: false,
            downloadConfig: { columnCalcs: false },
            renderHorizontal: "virtual",
            renderVertical: "virtual",

            filterMode: "remote", // We do not support flitering here, but for future it might be and then we need to do it remotely
            sortMode: "remote",
            ajaxSorting: true,
            ajaxURL: "/noop", // Must be set for version >5
            ajaxLoader: false,
            dataLoader: false,
            ajaxRequestFunc: async function () {
                if (me.isReady && !me.isLoading) {
                    me.isLoading = true;

                    const dataSorters = me.grid.getSorters();
                    const sorters = dataSorters.map((s: any) => {
                        const def = s.column?.getDefinition?.() ?? {};
                        return {
                            field: s.field,
                            dir: s.dir,
                            title: def.title ?? def.headerTooltip ?? def.field ?? s.field,
                        };
                    });

                    const { callee, function: fn } = me.context.onDataLoad;
                    const rawData = await callee.invokeMethodAsync(fn, sorters);
                    const data = Array.isArray(rawData)
                        ? rawData.map((r: any) => me.normalizeRowDeltaKeys(r))
                        : rawData;
                    me.isLoading = false;
                    return data;
                }
                return me.grid?.getData() ?? [];
            }
        };

        if (context.behaviorConfig?.highlightColor) {
            this.applyHighlightColor(context.behaviorConfig.highlightColor, context.id);
        }

        (Tabulator as any).extendModule("format", "formatters", {
            customNumber: (cell: any, formatterParams: any) => {
                return me.customNumber(cell, formatterParams);
            },
            customDatetime(cell: any, formatterParams: any) {
                return me.customDatetime(cell, formatterParams);
            },
            deltaNumber(cell: any, formatterParams: any) {
                return me.deltaNumber(cell, formatterParams);
            },
            linkBlank(cell: any, formatterParams: any) {
                return me.linkBlank(cell, formatterParams);
            }
        });

        this.grid = new Tabulator(this.ref, this.gridOptions);

        this.setGridListeners(this.grid, this.context.interactionEnabled, rowClickEnabled);

        this.isReady = true;
        this.isLoading = true;

        return this;
    }

    setGridListeners(grid: Tabulator, interactionEnabled: boolean, rowClickEnabled: boolean) {
        this.grid.on("dataTreeRowExpanded", this.dataTreeRowExpanded.bind(this));

        if (interactionEnabled) {
            this.grid.on('columnMoved', this.columnMoved.bind(this));
            this.grid.on('columnResized', this.columnResized.bind(this));

            if (rowClickEnabled) {
                this.grid.on('rowSelected', this.rowSelected.bind(this));
                this.grid.on('rowDeselected', this.rowDeselected.bind(this));
                //this.grid.on('rowSelectionChanged', this.rowSelectionChanged.bind(this));
                this.grid.on('dataProcessed', this.dataProcessed.bind(this));
            }
            // else {
            //     this.grid.on('cellClick', this.cellClick.bind(this));
            // }
        }

        //this.grid.on("cellClick", this.anchorCellClick.bind(this));
    }

    async dataProcessed(data: any[]) {

        await this.context.onDataProcessed.callee
            .invokeMethodAsync(this.context.onDataProcessed.function)
            .catch((e: any) => console.error(e));
    }

    async rowSelected(row: any) {
        var rowData = row.getData();

        let index = this.selectedRows.findIndex((v, i, o) => this.matchRow(rowData, i, o));
        if (!this.isSelecting && index == -1) {
            if (!this.context.behaviorConfig?.enableMultiSelect) {
                this.selectedRows = [];
            }
            this.selectedRows.push(row.getData());

            await this.rowSelectionChanged(this.selectedRows);
        }
    }

    async rowDeselected(row: any) {
        if (this.isSelecting)
            return;

        var rowData = row.getData();
        let index = this.selectedRows.findIndex((v, i, o) => this.matchRow(rowData, i, o));

        if (index > -1) {
            this.selectedRows.splice(index, 1);
            await this.rowSelectionChanged(this.selectedRows);
        } else {
            this.isSelecting = true;
            row.select();
            this.isSelecting = false;
        }
    }

    async rowSelectionChanged(data: any[]) {
        await this.context.onSelectedRows.callee
            .invokeMethodAsync(this.context.onSelectedRows.function, data, this.context.isPivot)
            .catch((e: any) => console.error(e));
    }

    //async rowSelectionChanged(data: any[], rows: any[]) {
    //    if (!this.isSelecting && !this.selectedRows.length) {
    //        this.selectedRows = data;

    //        //let selectedIndexes = rows.map((row: any) => {
    //        //    let allRows = row.getTable().getRows();
    //        //    return allRows.indexOf(row);
    //        //});

    //        await this.context.onSelectedRows.callee
    //            .invokeMethodAsync(this.context.onSelectedRows.function, data, this.context.isPivot)
    //            .catch((e: any) => console.error(e));
    //    }
    //}

    setIsSelecting(isSelecting: boolean) {
        this.isSelecting = isSelecting;
    }

    async setSelectedRow(index: number) {
        const rows = this?.grid?.getRows() ?? [];
        if (rows.length > index) {
            const row = this.grid.getRowFromPosition(index + 1);
            if (row) {
                await row.select();
            }
        }
    }

    async setSelectedRows(indices: number[]) {
        indices.forEach(async (i: any) => await this.setSelectedRow(i));
    }

    async setSelectedData(rows: any[]) {
        const tableRows = this.grid!.getRows();

        let matchedIndices = tableRows
            .map((row: any, index: number) => {
                let found = rows.some((x: any) =>
                    Object.keys(x)
                        .filter((k: any) => k.startsWith("key"))
                        .every((k: any) => x[k] === row.getData()[k])
                );
                return found ? index : -1;
            })
            .filter((index: number) => index !== -1);

        await this.setSelectedRows(matchedIndices);
    }

    deselectRow(index: any) {
        this.grid?.deselectRow(index);
    }

    deselectAllRows() {
        this.grid?.deselectRow();
        this.selectedRows = [];
    }

    async selectedRow(row: any) {
        const rows = row.getTable().getRows();
        await this.context.onSelectedRow.callee
            .invokeMethodAsync(this.context.onSelectedRow.function, rows.indexOf(row), row.getData(), this.context.isPivot);
    }

    //anchorCellClick(e: MouseEvent, cell: any) {
    //    if (e.button !== 0) return;
    //    const innerCell = (e.target as HTMLElement).closest('.tabulator-cell') as HTMLElement | null;
    //    const anchor = innerCell?.querySelector('a') as HTMLAnchorElement | null;
    //    if (!anchor) return;

    //    e.preventDefault();
    //    e.stopPropagation();

    //    const href = anchor.getAttribute("href");
    //    if (href) {
    //        window.open(href.replace(/%20/g, "-"), "_blank");
    //    }
    //}

    // cellClick(_e: any, cell: any) {
    //     const row = cell.getRow();
    //     const rows = row.getTable().getRows();
    //     const rowIndex = rows.indexOf(row);
    //
    //     if (_e.target instanceof HTMLAnchorElement) {
    //         _e.target.href = _e.target.href.split('%20').join('-');
    //         _e.target.target = '_blank';
    //         _e.target.textContent = _e.target.textContent;
    //         row.deselect();
    //         return;
    //     }
    //
    //     this.selectedRow(row);
    // }

    async dataTreeRowExpanded(row: any, level: any) {
        const levelFilterValues: string[] = [];

        //this.showLoaderOverlay();
        let current = row;
        let root = row;
        for (let i = 0; i < level + 1; i++) {
            levelFilterValues.unshift(
                current.getData()[this.context.defaultGroupFieldName]?.toString()
            );
            root = current;
            current = current.getTreeParent();
        }
        
        if (this.context.flatGroupColumnKeys?.length) {
            const rootData = root.getData();
            for (const key of this.context.flatGroupColumnKeys) {
                levelFilterValues.push(rootData[key]?.toString());
            }
        }

        let dataSorters = this.grid.getSorters();
        let sorters = dataSorters.map((s: any) => { return { field: s.field, dir: s.dir }; });

        await this.context.onDataTreeRowExpanded.callee
            .invokeMethodAsync(this.context.onDataTreeRowExpanded.function, levelFilterValues, level, sorters)
            .then((result: { hasMoreChildren: boolean; data: any[]; isAnchor: boolean; isUseIcon: boolean }) => {
                if (result.hasMoreChildren) result.data.forEach((d: any) => (d._children = []));
                row.update({ _children: result.data });

                if (result.isAnchor) {
                    //tabulator-data-tree-branch
                    //const margin = 47 + (level * 40); // Magic numbers
                    //const anchorInner = result.useIcon ? '<i class="material-icons">open_in_new</i>' : value;
                    //const anchor = `<div class="tabulator-data-tree-branch" style="margin-left:${margin}px"></div><a href="${value}" title="${value}" target="_blank">${anchorInner}</a>`;
                    //result.data[0][context.defaultGroupFieldName] = anchor;

                    const value = result.data[0][this.context.DefaultGroupFieldName];
                    result.data[0][this.context.DefaultGroupFieldName] =
                        `<a href="${value}" title="${value}">${result.isUseIcon ? '<i class="material-icons">open_in_new</i>' : value}</a>`;
                }
            });
        //.finally(() => this.hideLoaderOverlay());
    }

    async columnMoved(column: any, columns: any[]) {
        await this.context.onMovedColumn.callee
            .invokeMethodAsync(this.context.onMovedColumn.function, column.getField(), columns.indexOf(column));
    }

    async columnResized(column: any) {
        await this.context.onResizedColumn.callee
            .invokeMethodAsync(this.context.onResizedColumn.function, column.getField(), column.getWidth());
    }

    matchRow(value: any, index: number, objs: any[]) {
        const obj = objs.at(index);
        const dimensionKeys = this.context.dimensionKeyNames;

        const keysToCompare = dimensionKeys && dimensionKeys.length > 0
            ? dimensionKeys
            : Object.keys(value).filter((k: string) => k.startsWith('key'));

        return keysToCompare.every((k: string) => value[k] === obj[k]);
    }

    heightAdjuster(context: any) {
        let baseSize = 8;
        let pagingBlockSize = 52;

        return context.isPagingEnabled ? baseSize + pagingBlockSize : baseSize;
    }

    customNumber(cell: any, formatterParams: any) {
        let value = cell.getValue();
        let definition = this.lookupDefinition(cell);
        let cellFormatterParams = this.getFormatterParams(definition);
        let isBigValue = this.getBigValue(definition);
        let isNumberOverride = cell.getData()["_forceformatter"] && cell.getData()["_forceformatter"] == "number"; // number is so far the only one implemented
        let realValue = this.getRealValue(value, isBigValue);
        let symbol = cellFormatterParams.symbol;
        let numberSymbol = this.getNumberSymbol(cell, isBigValue, value);
        let valueFormatted = this.number_format(realValue, cellFormatterParams.precision, cellFormatterParams.decimal ?? definition.formatterParams.decimal, cellFormatterParams.thousand);
        let fixedBigNumberFormat = definition.formatterParams.fixedBigNumberFormat > 0 ? definition.formatterParams.fixedBigNumberFormat : this.context.fixedBigNumberFormat;

        if (symbol == "%" && !isNumberOverride)
            return this.deltaNumber(cell, formatterParams);

        if (definition.formatterParams.valuesOnLastGroupOnly && this.isBlank(value)) {
            if (value == "NullValueExpression")
                return definition.formatterParams.nullValueExpression;

            return "";
        }

        if (isNaN(Number(value)))
            return definition.formatterParams.nullValueExpression;

        if (this.isBlank(value))
            return definition.formatterParams.nullValueExpression;

        if (fixedBigNumberFormat > 0) {
            let prefix = definition.formatterParams?.prefix ?? this.context.numberFormat.formatterParams.prefix;

            switch (fixedBigNumberFormat) {
                case 1e+6:
                    numberSymbol = prefix.million;
                    break;
                case 1e+9:
                    numberSymbol = prefix.billion;
                    break;
                default:
                    numberSymbol = prefix.thousand;
                    break;
            }
            valueFormatted = this.number_format(value / fixedBigNumberFormat, cellFormatterParams.precision, cellFormatterParams.decimal ?? definition.formatterParams.decimal, cellFormatterParams.thousand);
        }

        return valueFormatted + numberSymbol;
    }

    deltaNumber(cell: any, formatterParams: any) {
        let cellValue = cell.getValue();
        let definition = this.lookupDefinition(cell);

        if (definition.formatterParams.valuesOnLastGroupOnly && this.isBlank(cellValue)) {
            return cellValue === "NullValueExpression" ? definition.formatterParams.nullValueExpression : "";
        }

        if (isNaN(Number(cellValue)) || this.isBlank(cellValue))
            return definition?.formatterParams.nullValueExpression;

        let value = cellValue * 100;
        let cellFormatterParams = this.getFormatterParams(definition);
        let valueFormatted = this.number_format(value, cellFormatterParams.precision, cellFormatterParams.decimal, cellFormatterParams.thousand);
        return valueFormatted + "%";
    }

    customDatetime(cell: any, formatterParams: any) {
        let cellValue = cell.getValue();
        if (!cellValue)
            return;

        let value = new Date(cellValue);
        let lang = !this.context.applicationLanguage || this.context.applicationLanguage.length === 0 ? navigator.language : this.context.applicationLanguage;
        let date = Intl.DateTimeFormat(lang, formatterParams.parameters).format(value);

        if (formatterParams.parameters.quarter)
            return `Q${Math.floor((value.getMonth() + 3) / 3)} ${date}`;

        if (formatterParams.parameters.week) {
            let oneJan = new Date(value.getFullYear(), 0, 1);
            let numberOfDays = Math.floor((value.valueOf() - oneJan.valueOf()) / (24 * 60 * 60 * 1000));
            return `${date}-W${Math.ceil((value.getDay() + 1 + numberOfDays) / 7)}`;
        }

        return date;
    }

    linkBlank(cell: any, formatterParams: any) {
        var url = cell.getValue();
        if (!url?.trim())
            return; // Empty link, do not create anchor

        var label = url; // Default label to the URL value

        // Create the anchor element
        var anchor = document.createElement('a');
        anchor.href = url;
        anchor.textContent = label;
        anchor.className = 'd-inline-block w-100';
        anchor.target = "_blank"; // Open in new tab
        //anchor.rel = "noopener noreferrer"; // Security best practice, but modern browsers add noopener auto
        anchor.innerHTML = '<i class="material-icons black-text d-inline w-100">open_in_new</i>';

        return anchor;
    }

    lookupDefinition(cell: any) {
        const def = cell?.getColumn()?.getDefinition?.();
        if (def?.formatterParams) return def;
        return this.lookupParentBottomCalcDefinition(cell);
    }

    lookupParentBottomCalcDefinition(cell: any) {
        try {
            const col = cell?.getColumn?.();
            const field = col?.getField?.();
            const parent = (col as any)?._column?.parent;
            const botCalcs = parent?.botCalcs as any[] | undefined;
            return botCalcs?.find?.((c: any) => c?.field === field)?.definition;
        } catch {
            return undefined;
        }
    }

    getFormatterParams(definition: any): { symbol: string, decimal: string, thousand: string, precision: number } {
        const columnDef = definition?.formatterParams ?? {};
        return {
            symbol: columnDef.symbol ?? columnDef.parameters.symbol,
            decimal: columnDef.decimal ?? columnDef.parameters.decimalSeparator,
            thousand: columnDef.thousand ?? columnDef.parameters.thousand,
            precision: columnDef.precision ?? columnDef.parameters.precision
        };
    }

    getPivotPercentCalculation(definition: any) {
        return definition?.formatterParams.isPivotPercentType;
    }

    getBigValue(definition: any) {
        return definition?.formatterParams.isBigValue
    }

    getNumberSymbol(cell: any, isBigValue: any, value: number) {
        //let columnDef = cell?._cell?.column?.definition;
        const columnDef = cell?.getColumn?.()?.getDefinition?.();
        const prefix = columnDef?.formatterParams?.prefix ?? this.context.numberFormat.formatterParams.prefix;
        const absoluteValue = Math.abs(value);

        if (isBigValue) {
            if (absoluteValue > 1e9) return prefix.billion;
            if (absoluteValue > 1e6) return prefix.million;
            if (absoluteValue > 1e3) return prefix.thousand;
        }
        return "";
    }

    getRealValue(value: number, isBigValue: boolean) {
        if (!isBigValue) return value;
        const abs = Math.abs(value);
        if (abs > 1e9) return value / 1e9;
        if (abs > 1e6) return value / 1e6;
        if (abs > 1e3) return value / 1e3;
        return value;
    }

    number_format(number: number, decimals: number, dec_point: string, thousands_sep: string) {
        const n = !isFinite(+number) ? 0 : +number;
        const prec = !isFinite(+decimals) ? 0 : Math.abs(decimals);
        const sep = (typeof thousands_sep === 'undefined') ? ',' : thousands_sep;
        const dec = (typeof dec_point === 'undefined') ? '.' : dec_point;

        const toFixedFix = (num: number, p: number) => {
            const k = Math.pow(10, p);
            return Math.round(num * k) / k;
        };

        const s = (prec ? toFixedFix(n, prec) : Math.round(n)).toString().split('.');
        if (s[0].length > 3) s[0] = s[0].replace(/\B(?=(?:\d{3})+(?!\d))/g, sep);
        if ((s[1] || '').length < prec) {
            s[1] = s[1] || '';
            s[1] += new Array(prec - s[1].length + 1).join('0');
        }
        return s.join(dec);
    }

    setColumns(columns: any[]) {
        let me = this;
        let context = me.context;

        let aggColumn: any = me.initAggColumn(context);
        if (me.context.sumColumns)
            me.setbottomCalc(aggColumn, context);

        let isChildColumn = columns.find((item: any) => item.cssClass?.includes("auto") ?? false);
        if (me.context.sumRows && !isChildColumn)
            columns.push(aggColumn);

        const isShares = columns.some((col: any) => !!col.isShares);
        columns.forEach((c: any) => me.setColumn(c, me, context, columns, isChildColumn, isShares));

        columns.forEach((c: any) => {
            delete c.isPercentRowCalculation;
            delete c.isPivotColumn;
            delete c.isShares;
        });
        me.grid?.setColumns(columns);

        this.isLoading = false;

        me.grid?.redraw(true);
    }

    setColumn(column: any, me: any, context: any, columns: any, isChildColumn: any, isShares: boolean) {
        const c: any = column;
        if (c.columns) this.setColumns(c.columns);

        const field = this.clean(c.field);
        const title = this.clean(c.title);

        const deltaHeader = this.clean(context.deltaHeader);
        const deltaValueHeader = this.clean(context.deltaValueHeader);

        const isDeltaPercent =
            field === deltaHeader ||
            title === deltaHeader ||
            field === this.DELTA_PERCENT_KEY;

        const isDeltaValue =
            field === deltaValueHeader ||
            title === deltaValueHeader ||
            field === this.DELTA_VALUE_KEY;
        
        if (isDeltaPercent) c.field = this.DELTA_PERCENT_KEY;
        if (isDeltaValue) c.field = this.DELTA_VALUE_KEY;

        if (!context?.sumColumns) return;

        const realColumns = columns.filter((col: any) => me.isRealColumn(col, me.context));
        const index = realColumns.indexOf(c);
        const columnIndex = columns.indexOf(c);
        const isNumberAggregation = c.formatterParams?._datatype === "Number" || c.field === me.context.aggregationFieldName;
        const isAggRowsColumn = c.field === me.context.aggregationFieldName;
        const isMeasureColumn = c.formatterParams?._datatype === "Number" && !isAggRowsColumn;
        const isDimenionsColumn = !(c.field && (c.field?.startsWith("key") ?? false));
        const isBottomCalc = c.visible && isNumberAggregation && isDimenionsColumn;

        if (isBottomCalc) {
            //if (c.title.includes(me.context.deltaHeader) && realColumns.length >= 3)

            if ((isDeltaPercent || isDeltaValue) && realColumns.length >= 3) {
                let currentColumn: any;
                let prevColumn: any;

                if (me.context.isPivot) {
                    currentColumn = me.context.pivotField.pivotDeltaCustom && me.context.pivotField.pivotDeltaCustomTo ? realColumns.find((c: any) => c.field.includes(me.context.pivotField.pivotDeltaCustomTo)) : realColumns[realColumns.length - 1];
                    prevColumn = me.context.pivotField.pivotDeltaCustom && me.context.pivotField.pivotDeltaCustomFrom ? realColumns.find((c: any) => c.field.includes(me.context.pivotField.pivotDeltaCustomFrom)) : realColumns[realColumns.length - 2];
                } else {
                    currentColumn = realColumns[2];
                    prevColumn = realColumns[1];
                }

                c.bottomCalc = (_values: any[], _data: any[]) => {
                    const prev = _data.reduce((acc: any, row: any) => acc + (Number(row[prevColumn.field]) || 0), 0);
                    const current = _data.reduce((acc: any, row: any) => acc + (Number(row[currentColumn.field]) || 0), 0);

                    if (isDeltaValue) {
                        return current - prev;
                    } else {
                        return ((current - prev) / prev);
                    }
                };

                c.bottomCalcFormatter = isDeltaValue ? context.numberFormat.type : "deltaNumber";
            } else if (isShares && isMeasureColumn) {
                c.bottomCalc = "sum";
                c.bottomCalcFormatter = "deltaNumber";
                return;
            }
            else {
                c.bottomCalc = columnIndex === 0 && !isChildColumn ? (_values: any[], _data: any[], _calcParams: any) => me.context.headerColumn : this.getColumnCalc(context.columnsAggregation);
                c.bottomCalcFormatter = context.numberFormat.type;
            }

            c.bottomCalcFormatterParams = {
                decimal: context.numberFormat.formatterParams.decimalSeparator,
                thousand: context.numberFormat.formatterParams.thousand,
                precision: context.numberFormat.formatterParams.precision,
                symbol: context.numberFormat.formatterParams.symbol,
                symbolAfter: "p"
            };
        } else
            c.bottomCalc = columnIndex === 0 ? (_values: any[], _data: any[], _calcParams: any) => me.context.headerColumn
                : (_values: any[], _data: any[], _calcParams: any) => "";
    }

    isRealColumn(column: any, context: any) {
        const field = this.clean(column.field);
        const title = this.clean(column.title);

        const deltaHeader = this.clean(context.deltaHeader);
        const deltaValueHeader = this.clean(context.deltaValueHeader);

        const isDeltaPercent =
            field === deltaHeader ||
            title === deltaHeader ||
            field === this.DELTA_PERCENT_KEY;

        const isDeltaValue =
            field === deltaValueHeader ||
            title === deltaValueHeader ||
            field === this.DELTA_VALUE_KEY;

        const sortFields = (context.sortFields ?? []).map((x: any) => this.clean(x));
        const isSortField = sortFields.includes(title ?? field);

        const isAggRow = field === context.aggregationFieldName;

        return !(isSortField || isAggRow || isDeltaValue || isDeltaPercent);
    }

    initAggColumn(context: any) {
        return {
            title: context.headerRow,
            field: context.aggregationFieldName,
            cssClass: "rows-summarize", headerHozAlign: "right", hozAlign: "right",
            width: context.rowSummarizeColumnWidth,
            minWidth: 150,
            formatter: context.numberFormat.type,
            visible: true,
            headerSort: !context.rowsAggregationSortDisabled,
            formatterParams: {
                decimal: context.numberFormat.formatterParams.decimalSeparator,
                thousand: context.numberFormat.formatterParams.thousand,
                precision: context.numberFormat.formatterParams.precision,
                symbol: context.numberFormat.formatterParams.symbol,
                symbolAfter: "p",
                isPivotPercentType: context.isPivotPercentType,
                isBigValue: context.isBigValue,
            }
        };
    }

    setbottomCalc(aggColumn: any, context: any) {
        aggColumn.bottomCalc = this.getColumnCalc(context.columnsAggregation);
        aggColumn.bottomCalcFormatter = context.numberFormat.type;
        aggColumn.bottomCalcFormatterParams = {
            decimal: context.numberFormat.formatterParams.decimalSeparator,
            thousand: context.numberFormat.formatterParams.thousand,
            symbol: context.numberFormat.formatterParams.symbol,
            symbolAfter: "p",
            precision: context.numberFormat.formatterParams.precision
        };
    }

    getColumnCalc(fn: string): "avg" | "max" | "min" | "sum" | "concat" | "count" | ((values: any[], data: any[], calcParams: {}) => any) {
        const _fn = (fn ?? "sum").toLowerCase();
        switch (_fn) {
            case "median":
                return (values: any[], data: any[]) => {
                    this.deleteObjects(data);
                    const mid = Math.floor(values.length / 2);
                    const nums = values.slice().sort((a, b) => a - b);
                    return nums.length % 2 !== 0 ? nums[mid] : (nums[mid - 1] + nums[mid]) / 2;
                };
            case "avg":
            case "max":
            case "min":
            case "sum":
            case "concat":
            case "count":
                return _fn;
            default:
                return "sum";
        }
    }

    deleteObjects(data: any[]) {
        data.forEach((obj: any) =>
            Object.keys(obj).forEach((key: any) => {
                if (String(key).startsWith("key"))
                    delete obj[key];
            })
        );
    }

    async setHeaderClick(e: any, column: any) {
        const columnDef = column.getDefinition?.();
        if (columnDef?.headerSort) {
            const field = columnDef.field;
            const title = columnDef.formatterParams._fieldName;
            this.showLoaderOverlay();
            this.grid?.setData?.([]);
            await this.context.onSortedCaller.invokeMethodAsync(this.context.onSortedDataFn, field, title);
            this.hideLoaderOverlay();
        }
    }

    getSortFields(column: any) {
        const sorters = this.grid?.getSorters?.().map((s: any) => ({ field: s.field, dir: s.dir })) ?? []; // All sorters except the one in column if this is newly selected (not a reorder)
        if (sorters.findIndex((s: any) => s.field === column.field) === -1) {
            sorters.push({ field: column.field, dir: column.dir });
        }
        return sorters;
    }

    export(downloadType: any, fileName: string) {
        this.grid?.download?.(downloadType, fileName, {
            documentProcessing: (wb: any) => {
                for (const prop in wb.Sheets.Sheet1) {
                    const date_regex = /\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d(?:\.\d+)?Z?/gm;
                    if (date_regex.test(wb.Sheets.Sheet1[prop].v))
                        wb.Sheets.Sheet1[prop].t = "d";
                    else if (wb.Sheets.Sheet1[prop].v == 'null')
                        wb.Sheets.Sheet1[prop].v = '';
                }
                return wb;
            }
        });
    }

    сopyDataToClipboard() {
        this.grid?.copyToClipboard?.();
    }

    resizeToParent() {
        const elRect = this.ref.parentElement.getBoundingClientRect();
        const height = (elRect.height - this.heightAdjuster(this.context)) + 'px';
        const width = elRect.width + 'px';
        const el = this.ref as HTMLElement;
        el.style.width = width;
        el.style.maxHeight = height;
        el.style.display = 'inline-block';
        this.grid?.redraw?.();
    }

    setSize() {
        const elRect = this.ref.parentElement.getBoundingClientRect();
        const height = (elRect.height - this.heightAdjuster(this.context)) + 'px';
        const width = elRect.width + 'px';
        const el = this.ref as any;
        el.style.width = width;
        el.maxHeight = height;
        el.style.display = 'inline-block';
        this.grid?.redraw?.();
    }

    destroyGrid() {
        if (this.onResize) window.removeEventListener('resize', this.onResize);
        this.grid?.destroy?.();
    }

    updateProperties(context: any): boolean {
        let hasIsPivotChanged = context.isPivot !== this.context.isPivot;
        let hasGroupingChanged = context.grouping !== this.context.grouping;
        let hasGroupTreeChildIndentChanged = context.groupTreeChildIndent !== this.context.groupTreeChildIndent;
        let hasSumColumnsChanged = context.sumColumns !== this.context.sumColumns;
        let hasLayoutChanged = context.layout !== this.context.layout;
        let hasInteractionEnabledChanged = context.interactionEnabled !== this.context.interactionEnabled;
        let hasEnableRowHighlightChanged = context.behaviorConfig?.rowClickEnabled !== this.context.behaviorConfig?.rowClickEnabled;
        let hasHighlightColorChanged = context.behaviorConfig?.highlightColor !== this.context.behaviorConfig?.highlightColor;

        for (const property in context) {
            (this.context as any)[property] = context[property];
        }

        return hasIsPivotChanged || hasGroupingChanged || hasGroupTreeChildIndentChanged || hasSumColumnsChanged || hasLayoutChanged || hasInteractionEnabledChanged || hasEnableRowHighlightChanged || hasHighlightColorChanged;
    }

    updateContext(context: any) {
        if (this.updateProperties(context)) {
            this.destroyGrid();
            this.createGridByRef(this.ref, this.context);
        }
        else {
            this.grid?.redraw(true);
        }
    }

    updateHeaderRow(headerRow: any) {
        if (this?.context) {
            this.context.headerRow = headerRow;
        }
    }

    updateHeaderColumn(headerColumn: any) {
        if (this?.context) {
            this.context.headerColumn = headerColumn;
        }
    }

    setMultipleSorter(orderdFields: any[]) {
        if (this.isLoading) return;

        const sorters = (orderdFields ?? [])
            .map((f: any) => {
                const rn = this.clean(f.resultName);
                const isDelta = rn === this.DELTA_VALUE_KEY || rn === this.DELTA_PERCENT_KEY;
                const column = rn;
                const dir = this.getDirectionName(f.order);

                if (!column || dir === "none") return null;

                return { column, dir };
            })
            .filter((x: any) => x !== null);
        this.grid?.setSort(sorters);
    }

    getDirectionName(orderNum: number): string {
        switch (orderNum) {
            case 1:
                return "asc"
            case 2:
                return "desc"
            default:
                return "none"
        }
    }

    formatValue(cell: any) {
        if (this.isValidDate(cell))
            return this.dateToString(cell.value);

        if (this.isValidNumber(cell)) {
            if (Number.isInteger(cell.value)) return this.intToString(cell.value);
            const numDecimals = this.context?.numDecimals ?? 2;
            return this.numberToString(cell.value, numDecimals);
        }
        return cell.value;
    }

    dateToString(value: any) {
        let date = new Date(value);
        return date.toLocaleDateString();
    }

    intToString(value: any) {
        return (Math.round(value * 100) / 100).toLocaleString(navigator.language);
    }

    numberToString(value: any, numDecimals: number) {
        return Intl.NumberFormat(navigator.language, {
            style: 'decimal',
            maximumFractionDigits: numDecimals,
            minimumFractionDigits: numDecimals
        }).format(value)
    }

    setNumDecimal(value: any, numDecimals: number) {
        return value.toFixed(numDecimals);
    }

    isValidDate(cell: any) {
        if (cell.colDef && cell.colDef.formatterParams._datatype && cell.colDef.formatterParams._datatype !== 'DateTime') {
            return false;
        }
        return !this.isValidNumber(cell) && Date.parse(cell.value);
    }

    isValidNumber(cell: any) {
        return typeof cell.value === 'number' && isFinite(cell.value);
    }

    showLoaderOverlay() {
        //this.ref.previousElementSibling.style.display = 'block';
        this.ref.parentElement.firstElementChild.style.display = 'block';
    }

    hideLoaderOverlay() {
        //this.ref.previousElementSibling.style.display = 'none';
        this.ref.parentElement.firstElementChild.style.display = 'none';
    }

    isBlank(value: any) {
        return value?.length == 0 || value == null || value == undefined;
    }

    applyHighlightColor(highlightColor: string, id: any) {
        const existingStyle = document.getElementById(`tabulator-highlight-style-${id}`);
        if (existingStyle) {
            existingStyle.remove();
        }

        const style = document.createElement('style');
        style.id = `tabulator-highlight-style-${id}`;
        style.textContent = `

            [id="${id}"] .tabulator .tabulator-tableholder .tabulator-table .tabulator-row.tabulator-selected {
                background-color: ${highlightColor} !important;
                background: ${highlightColor} !important;
            }
            [id="${id}"] .tabulator .tabulator-tableholder .tabulator-table .tabulator-row.tabulator-selected:hover {
                background-color: ${highlightColor} !important;
                background: ${highlightColor} !important;
                opacity: 0.9 !important;
            }
            [id="${id}"] .tabulator .tabulator-tableholder .tabulator-table .tabulator-row.tabulator-selected .tabulator-cell {
                background-color: ${highlightColor} !important;
                background: ${highlightColor} !important;
            }
            [id="${id}"] .tabulator .tabulator-tableholder .tabulator-table .tabulator-row.tabulator-selected.tabulator-row-odd {
                background-color: ${highlightColor} !important;
                background: ${highlightColor} !important;
            }
            [id="${id}"] .tabulator .tabulator-tableholder .tabulator-table .tabulator-row.tabulator-selected.tabulator-row-even {
                background-color: ${highlightColor} !important;
                background: ${highlightColor} !important;
            }
        `;
        document.head.appendChild(style);
    }
    
    private stripZeroWidth(s: string) {
        return s.replace(/[\u200B-\u200D\uFEFF]/g, "");
    }

    private clean(v: any) {
        return typeof v === "string" ? this.stripZeroWidth(v).trim() : v;
    }
    
    normalizeRowDeltaKeys(row: any) {
        if (!row || typeof row !== "object") return row;

        if (this.DELTA_PERCENT_KEY in row && this.DELTA_VALUE_KEY in row) return row;

        const out = row;
        const deltaPercentTitle = this.clean(this.context?.deltaHeader);
        const deltaValueTitle = this.clean(this.context?.deltaValueHeader);

        for (const k of Object.keys(row)) {
            const cleaned = this.clean(k);
            if ((cleaned === deltaPercentTitle || cleaned === this.DELTA_PERCENT_KEY) && !(this.DELTA_PERCENT_KEY in out))
                out.DeltaPercent = row[k];

            if ((cleaned === deltaValueTitle || cleaned === this.DELTA_VALUE_KEY) && !(this.DELTA_VALUE_KEY in out))
                out.DeltaValue = row[k];
        }
        return out;
    }
}